import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import { TabContext, TabList, TabPanel } from "@mui/lab"
import {
    Badge,
    Box,
    Chip,
    Stack,
    Tab,
    Typography,
    styled,
    useTheme,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import {
    ReactElement,
    SyntheticEvent,
    useCallback,
    useMemo,
    useState,
} from "react"

import ConfigurationChanges from "./configurationChanges"
import CurrentConfiguration from "./currentConfiguration"
import NotAuthorised from "../../../components/fallback/notAuthorised"
import AppTooltip from "../../../components/helper/appTooltip"
import HelpIcon from "../../../components/helper/helpIcon"
import AppPaper from "../../../components/surfaces/appPaper"
import { DNO_THRESHOLD_MANAGER } from "../../../constants/roles"
import {
    TT_THRESHOLD_CONFIG_CHANGES,
    TT_THRESHOLD_CURRENT_CONFIG,
    TT_THRESHOLD_MANAGEMENT_PAGE,
} from "../../../constants/tooltips"
import { useGetUserData } from "../../../hooks/useGetUserData"
import useGetChangeRequests from "../../../services/middleware/hooks/dnoThreshold/useGetChangeRequests"
import {
    BUTTON_RADIUS_MULTIPLIER,
    GRID_SPACING,
} from "../../../themes/defaultThemeParams"

const AppTab = styled(Tab)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * BUTTON_RADIUS_MULTIPLIER,
    textTransform: "none",
}))

const CURRENT_CONFIG_TAB = "CURRENT_CONFIG"
const CHANGES_TAB = "CHANGES"

/**
 * DNO Threshold Management page.
 * Tab 1: Current Configuration (read-only view of DNO config and thresholds)
 * Tab 2: Configuration Changes (change history, initiate/approve/reject changes)
 * Current Configuration is only visible to Threshold Managers.
 * The page itself is accessible to all DNO Approved users.
 */
const ThresholdManagement = (): ReactElement => {
    const theme = useTheme()
    const { groups, username, isIDno } = useGetUserData()
    const isThresholdManager = groups.includes(DNO_THRESHOLD_MANAGER)

    const { data: changeRequestsData } = useGetChangeRequests()

    const hasPendingChange = useMemo(
        () =>
            changeRequestsData?.changeRequests?.some(
                (cr) => cr.status === "PENDING_APPROVAL"
            ) ?? false,
        [changeRequestsData]
    )

    const defaultTab = isThresholdManager ? CHANGES_TAB : CURRENT_CONFIG_TAB
    const [tabSelection, setTabSelection] = useState(defaultTab)

    const [buttonSlotEl, setButtonSlotEl] = useState<HTMLDivElement | null>(
        null
    )
    const buttonSlotRef = useCallback((node: HTMLDivElement | null): void => {
        setButtonSlotEl(node)
    }, [])

    const handleChange = (
        _event: SyntheticEvent,
        newTabSelection: string
    ): void => {
        setTabSelection(newTabSelection)
    }

    if (isIDno) return <NotAuthorised />
    return (
        <Grid container spacing={GRID_SPACING}>
            <Grid xs={12}>
                <AppPaper
                    sx={{
                        minHeight: "50px",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flexWrap: "wrap",
                        py: theme.spacing(1),
                    }}
                >
                    <Typography variant="h6" component="div">
                        <Box display="inline">View and Manage your </Box>
                        <Box
                            color={theme.palette.primary.main}
                            display="inline"
                        >
                            Thresholds and Configuration
                        </Box>
                    </Typography>
                    <HelpIcon tooltip={TT_THRESHOLD_MANAGEMENT_PAGE} />
                    <Box sx={{ flexGrow: 1 }} />
                    {isThresholdManager && (
                        <Box ref={buttonSlotRef} sx={{ flexShrink: 0 }} />
                    )}
                </AppPaper>
            </Grid>
            <Grid xs={12}>
                <AppPaper>
                    <TabContext value={tabSelection}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                            }}
                        >
                            <TabList
                                aria-label="Threshold Management Tabs"
                                onChange={handleChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ flexGrow: 1, flexShrink: 0 }}
                            >
                                <AppTab
                                    label={
                                        <AppTooltip
                                            title={TT_THRESHOLD_CURRENT_CONFIG}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight="500"
                                            >
                                                Current Configuration
                                            </Typography>
                                        </AppTooltip>
                                    }
                                    value={CURRENT_CONFIG_TAB}
                                />
                                <AppTab
                                    label={
                                        <AppTooltip
                                            title={TT_THRESHOLD_CONFIG_CHANGES}
                                        >
                                            <Badge
                                                color="warning"
                                                variant="dot"
                                                invisible={
                                                    !isThresholdManager ||
                                                    !hasPendingChange
                                                }
                                                sx={{ pr: 1 }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="500"
                                                >
                                                    Configuration Changes
                                                </Typography>
                                            </Badge>
                                        </AppTooltip>
                                    }
                                    value={CHANGES_TAB}
                                />
                            </TabList>
                            {tabSelection === CHANGES_TAB && (
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{
                                        ml: { xs: 0, sm: "auto" },
                                        px: 1.5,
                                        py: { xs: 1, sm: 0 },
                                        pr: { xs: 1.5, sm: 5 },
                                        flexShrink: 0,
                                        width: { xs: "100%", sm: "auto" },
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        fontSize={20}
                                    >
                                        Key:
                                    </Typography>
                                    <Stack spacing={1}>
                                        <Chip
                                            icon={<PersonOutlineIcon />}
                                            label="Raised by"
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            icon={<CheckCircleOutlineIcon />}
                                            label="Reviewed by"
                                            size="small"
                                            variant="filled"
                                        />
                                    </Stack>
                                </Stack>
                            )}
                        </Box>
                        <TabPanel value={CURRENT_CONFIG_TAB}>
                            <CurrentConfiguration
                                isThresholdManager={isThresholdManager}
                            />
                        </TabPanel>
                        <TabPanel value={CHANGES_TAB}>
                            <ConfigurationChanges
                                isThresholdManager={isThresholdManager}
                                username={username}
                                buttonSlotEl={buttonSlotEl}
                            />
                        </TabPanel>
                    </TabContext>
                </AppPaper>
            </Grid>
        </Grid>
    )
}

export default ThresholdManagement
